import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, ExternalLink } from 'lucide-react'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'

// ---------------------------------------------------------------------------
// API token connect modal (used when OAuth is not configured)
// ---------------------------------------------------------------------------

function BitbucketCredentialsModal({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  onComplete: () => void
}) {
  const [isSaving, setIsSaving] = useState(false)
  const [identifier, setIdentifier] = useState('')
  const [apiToken, setApiToken] = useState('')
  const [workspaceSlug, setWorkspaceSlug] = useState('')

  const handleSave = async () => {
    if (!identifier.trim()) { toast.error('Atlassian email is required'); return }
    if (!apiToken.trim()) { toast.error('API token is required'); return }
    if (!workspaceSlug.trim()) { toast.error('Workspace slug is required'); return }

    setIsSaving(true)
    try {
      await trpcClient.integrations.createBitbucketWithApiToken.mutate({
        identifier: identifier.trim(),
        apiToken: apiToken.trim(),
        workspaceSlug: workspaceSlug.trim(),
      })
      toast.success('Bitbucket connected')

      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      onOpenChange(false)
      onComplete()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to connect Bitbucket')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IntegrationIcon type="Bitbucket" />
            Connect Bitbucket
          </DialogTitle>
          <DialogDescription>
            Connect using an Atlassian API token for compliance scanning and repository access.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4 py-2 px-6" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-2">
            <Label htmlFor="bb-identifier">Atlassian email</Label>
            <Input
              id="bb-identifier"
              name="bitbucket-identifier"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="you@example.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <p className="text-[11px] text-text-subtle">
              Use the email address for your Atlassian account. Bitbucket usernames are not accepted for API tokens.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bb-api-token">API token</Label>
            <Input
              id="bb-api-token"
              name="bitbucket-api-token"
              type="password"
              autoComplete="new-password"
              placeholder="Bitbucket API token"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
            />
            <p className="text-[11px] text-text-subtle">
              Create a Bitbucket API token with read:user:bitbucket, read:workspace:bitbucket, read:repository:bitbucket, read:project:bitbucket, read:pullrequest:bitbucket, and read:issue:bitbucket scopes.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bb-workspace">Workspace slug</Label>
            <Input
              id="bb-workspace"
              name="bitbucket-workspace"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="your-workspace"
              value={workspaceSlug}
              onChange={(e) => setWorkspaceSlug(e.target.value)}
            />
            <p className="text-[11px] text-text-subtle">
              The workspace slug from bitbucket.org/your-workspace
            </p>
          </div>

          <a
            href="https://support.atlassian.com/bitbucket-cloud/docs/api-token-permissions/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] text-cobalt-strong hover:underline"
          >
            How to create an API token
            <ExternalLink className="h-3 w-3" />
          </a>
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Config modal router — picks credentials modal vs manage dialog
// ---------------------------------------------------------------------------

function BitbucketConfigModalWrapper(props: ConfigModalProps) {
  const { open, onOpenChange, integration, workspaceId, onComplete } = props

  if (integration) {
    return null
  }

  return (
    <BitbucketCredentialsModal
      open={open}
      onOpenChange={onOpenChange}
      workspaceId={workspaceId}
      onComplete={onComplete}
    />
  )
}

// ---------------------------------------------------------------------------
// Provider export
// ---------------------------------------------------------------------------

export const BitbucketProvider: IntegrationProvider = {
  id: 'Bitbucket',
  name: 'Bitbucket',
  description: 'Connect Bitbucket to sync documents, PRs & Issues',
  Icon: () => <IntegrationIcon type="Bitbucket" />,
  ConfigModal: BitbucketConfigModalWrapper,

  handleConnect: async ({ redirectUrl, onSuccess, onFinish }) => {
    const typesData =
      queryClient.getQueryData(trpc.integrations.getAvailableIntegrationTypes.queryKey()) ??
      (await queryClient.fetchQuery(trpc.integrations.getAvailableIntegrationTypes.queryOptions()))
    const authMethod = (typesData as Array<{ id: string; authMethod?: string }>)?.find((t) => t.id === 'Bitbucket')?.authMethod

    if (authMethod === 'oauth') {
      try {
        const result = await trpcClient.integrations.initBitbucketAuth.mutate({
          features: ['issues', 'prs', 'code'],
          redirectUrl:
            redirectUrl ||
            `${window.location.origin}/auth-callback/Bitbucket`,
          providerType: 'Bitbucket',
        })

        openOAuthConnectPopup({
          authUrl: result.authUrl,
          windowName: 'bitbucket-auth',
          provider: 'Bitbucket',
          onSuccess,
          onError: (message) =>
            toast.error(`Bitbucket authentication failed: ${message}`),
          onPopupBlocked: () =>
            toast.error(
              'OAuth popup was blocked. Allow popups and try again.'
            ),
          onFinish,
        })
      } catch (error) {
        console.error('Error initiating Bitbucket OAuth:', error)
        toast.error('Failed to start Bitbucket OAuth. Please try again.')
        onFinish?.('error')
      }
      return
    }

    onSuccess?.({ provider: 'Bitbucket' })
  },

  onConnectComplete: async ({ code, state }) => {
    return trpcClient.integrations.completeBitbucketAuth.mutate({ code, state })
  },
}
